import { cn } from '@newfront-insurance/core-ui/v2';
import { X } from 'lucide-react';

export type NovaAlertVariant = 'error' | 'warning' | 'info';

export interface NovaAlertAction {
  label: string;
  onClick: () => unknown;
}

export interface NovaAlertProps {
  variant: NovaAlertVariant;
  message: string;
  label?: string;
  action?: NovaAlertAction;
  onDismiss?: () => void;
  className?: string;
}

const variantStyles: Record<NovaAlertVariant, string> = {
  error: 'bg-fire-550 text-white',
  warning: 'bg-glitter-300 text-umber-500',
  info: 'bg-aqua-150 text-aqua-600',
};

const defaultLabels: Record<NovaAlertVariant, string> = {
  error: 'Error:',
  warning: 'Warning:',
  info: 'Info:',
};

export function NovaAlert({
  variant,
  message,
  label = defaultLabels[variant],
  action,
  onDismiss,
  className,
}: NovaAlertProps) {
  return (
    <div
      role="alert"
      data-testid="nova-alert"
      className={cn(
        'inline-flex items-center justify-center gap-[10px] rounded-md p-[10px]',
        'shadow-[2px_2px_16px_0_rgba(38,45,70,0.15)]',
        'text-xs leading-4 font-semibold',
        variantStyles[variant],
        className,
      )}
    >
      <span>
        {label && <span>{label} </span>}
        {message}
      </span>
      {action && (
        <button
          data-testid="nova-alert-action"
          type="button"
          className="shrink-0 cursor-pointer border-0 bg-transparent p-0 underline underline-offset-2 hover:opacity-80"
          onClick={() => void action.onClick()}
        >
          {action.label}
        </button>
      )}
      {onDismiss && (
        <button
          data-testid="nova-alert-dismiss"
          type="button"
          aria-label="Dismiss alert"
          className="shrink-0 cursor-pointer border-0 bg-transparent p-0 opacity-60 hover:opacity-100"
          onClick={onDismiss}
        >
          <X aria-hidden="true" className="size-4" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}
